import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import { Layout } from "./components/Layout";
import { Dashboard } from "./components/Dashboard";
import { WalletPage } from "./components/WalletPage";
import { AddFundsPage } from "./components/AddFundsPage";
import { TransferPage } from "./components/TransferPage";
import AnalyticsPage from "./components/AnalyticsPage";
import { TermsPage } from "./components/TermsPage";
import { FAQPage } from "./components/FAQPage";
import Login from "./components/Login";
import { useDispatch } from "react-redux";
import { setUser } from "./slices/userSlices";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import PaymentRequestPage from "./components/PaymentRequestPage";
import { GridLoader } from "react-spinners";

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { ready, authenticated } = usePrivy();

  if (!ready) return null;
  if (!authenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

// Component to handle smooth scroll on route change
const ScrollToTop: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

function App() {
  const { ready, authenticated, user } = usePrivy();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true); // New loading state

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      if (ready && authenticated) {
        const userId = user?.email?.address || user?.wallet?.address;
        if (!userId) {
          setIsLoading(false);
          return;
        }

        try {
          const userRef = doc(db, "users", userId);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            const userData = userSnap.data();
            dispatch(
              setUser({
                uid: userId,
                email: user?.email?.address || null,
                wallet: user?.wallet?.address || null,
                username: userData.username,
                joinedAt: userData.joinDate?.toDate?.().toISOString() || null,
                phone: null,
              } as any)
            );
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [ready, authenticated, user, dispatch]);

  // Show loading spinner while fetching user data
  if (!ready || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16]">
        <GridLoader color="#61CE70" size={20} />
      </div>
    );
  }

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={<Layout />}>
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="wallet"
            element={
              <ProtectedRoute>
                <WalletPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="add-funds"
            element={
              <ProtectedRoute>
                <AddFundsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="transfer"
            element={
              <ProtectedRoute>
                <TransferPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="analytics"
            element={
              <ProtectedRoute>
                <AnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="paymentRequests"
            element={
              <ProtectedRoute>
                <PaymentRequestPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="terms"
            element={
              <ProtectedRoute>
                <TermsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="faqs"
            element={
              <ProtectedRoute>
                <FAQPage />
              </ProtectedRoute>
            }
          />
          <Route index element={<Navigate to="dashboard" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
